import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import { getPurchaseOrdersApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler, debounce } from 'utils/common';
import { getStatusBadge, poStatusOptions } from '../../constants';
import PageHeader from 'components/PageHeader';
import ListShell from 'components/ListShell';
import DateField from 'components/Form/dateField';
import POFormModal from './POFormModal';
import useStickyIdentity from '../../components/useStickyIdentity';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

const POList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Lines handed over by another screen (the replacement report's "Reorder")
  // to seed a new PO. See POFormModal's `prefill`.
  const [prefill, setPrefill] = useState(null);

  // Consumed exactly once: the state is cleared from history the moment it
  // is read, so a refresh or Back cannot re-open a modal for lines the user
  // already ordered — or already decided not to.
  useEffect(() => {
    const lines = location.state?.prefillLines;
    if (!lines?.length) return;
    setPrefill({ lines });
    setShowCreateModal(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  // "Latest request wins" guard, covering the loading reset too, so a slow
  // response for a filter combination the user has since changed away from
  // (e.g. rapid date-range edits) can't land after a newer one and repaint
  // the table with stale rows or leave `loading` stuck.
  const reqIdRef = useRef(0);

  const debouncedSetSearch = useMemo(() => debounce((value) => {
    setSearch(value);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  }, 400), []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  const fetchData = useCallback(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const params = { page: pageSettings.page, page_size: pageSettings.pageSize };
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    getPurchaseOrdersApi(params).then((response) => {
      if (reqId !== reqIdRef.current) return;
      setData(response.data?.results || []);
      setPageSettings((prev) => ({ ...prev, total: response.data?.count || 0 }));
    }).catch((e) => {
      if (reqId !== reqIdRef.current) return;
      toast.error(uniformErrorMessage(e, 'Failed to load purchase orders'), toastOpts);
    }).finally(() => {
      if (reqId === reqIdRef.current) setLoading(false);
    });
  }, [pageSettings.page, pageSettings.pageSize, statusFilter, search, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const goToDetail = (id) => navigate(`/uniform-management/purchase-orders/${id}`);

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Pinned: the PO number is what names the row; eight columns scroll.
      title: 'PO Number', dataIndex: 'po_number', ...pinLeft(160),
      render: (text, record) => (
        <button type="button" className="table-link" onClick={() => goToDetail(record.id)}>{text}</button>
      )
    },
    { title: 'PO Date', dataIndex: 'po_date', render: (d) => formatDateHandler(d) },
    { title: 'Supplier', dataIndex: 'supplier_name', render: (v) => v || '-' },
    { title: 'Lines', dataIndex: 'line_count' },
    {
      title: 'Ordered / Received',
      render: (_, record) => `${record.ordered_total ?? 0} / ${record.received_total ?? 0}`
    },
    {
      title: 'Documents',
      render: (_, record) => (
        <div className="d-flex gap-2">
          <i className={`fa fa-file-lines ${record.has_po_document ? 'text-success' : 'text-muted'}`}
            title={record.has_po_document ? 'PO document attached' : 'No PO document'} />
          <i className={`fa fa-file-invoice ${record.has_invoice ? 'text-success' : 'text-muted'}`}
            title={record.has_invoice ? 'Invoice attached' : 'No invoice'} />
        </div>
      )
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status.replace('_', ' ')}</span>
    },
    { title: 'Created By', dataIndex: 'created_by_name' },
  ];

  const filterItems = [
    { key: '', label: 'All' },
    ...poStatusOptions.map((o) => ({ key: o.value, label: o.label }))
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title="Purchase Orders"
          actions={
            <Button label="New PO" icon="fa fa-plus" className="btn btn-primary btn-sm"
              onClick={() => setShowCreateModal(true)} />
          }
        >
          <div className="d-flex align-items-center flex-wrap gap-2">
            <input className="form-control" style={{ width: 240 }} placeholder="Search PO number, supplier..."
              aria-label="Search purchase orders by PO number or supplier"
              value={searchInput} onChange={handleSearchChange} />
            <DateField style={{ width: 170 }} value={dateFrom} max={dateTo}
              aria-label="PO date from"
              onChange={(v) => { setDateFrom(v); setPageSettings((prev) => ({ ...prev, page: 1 })); }} />
            <span className="text-muted">to</span>
            <DateField style={{ width: 170 }} value={dateTo} min={dateFrom}
              aria-label="PO date to"
              onChange={(v) => { setDateTo(v); setPageSettings((prev) => ({ ...prev, page: 1 })); }} />
          </div>
        </PageHeader>

        <ListShell
          filters={filterItems}
          activeKey={statusFilter}
          onFilterChange={(key) => { setStatusFilter(key); setPageSettings((prev) => ({ ...prev, page: 1 })); }}
          resultCount={pageSettings.total}
          loading={loading}
        >
          <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
            pagination={{
              total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
              pageSizeOptions, itemRender, showSizeChanger: true
            }}
            rowClassName="clickable-row"
            onRow={(record) => ({
              onClick: (e) => {
                if (e.target.closest('a, button')) return;
                goToDetail(record.id);
              },
            })}
            onChange={handleChange} scroll={{ x: 'max-content' }} />
        </ListShell>

        <POFormModal
          open={showCreateModal}
          prefill={prefill}
          onClose={() => { setShowCreateModal(false); setPrefill(null); }}
          onSaved={() => { setShowCreateModal(false); setPrefill(null); fetchData(); }}
        />
      </div>
    </div>
  );
};

export default POList;
